import { useState, useEffect, useCallback, useRef } from 'react';
import useSelectedDomain from '../service/Hooks/useSelectedDomain';
import getMouvementsFinanciers from '../service/api/AccountHistoricService';
import { FaSyncAlt, FaWifi } from 'react-icons/fa';
import TableSkeleton from '../admin/TableSkeleton';

import MouvementFilters from './components/MouvementFilters';
import MouvementTableRow from './components/MouvementTableRow';
import MouvementPagination from './components/MouvementPagination';
import DocumentViewerModal from './components/Modal/DocumentViewerModal';

const MouvementFinancierTable = () => {
  const selectedDomain = useSelectedDomain();
  
  const [mouvements, setMouvements] = useState([]);
  const [loading, setLoading] = useState(false); 
  const [initialLoading, setInitialLoading] = useState(true); 
  const [isOffline, setIsOffline] = useState(false);
  const [error, setError] = useState(null);
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0
  });
  
  // ✅ État pour les filtres SANS déclencher de fetch immédiat
  const [filters, setFilters] = useState({
    accountName: '',
    securityName: '',
    categorie: '',
    sortOrder: ''
  });

  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedHistoric, setSelectedHistoric] = useState(null);
  
  // ✅ REF pour éviter les appels multiples
  const fetchTimeoutRef = useRef(null);
  const isFirstRender = useRef(true);
  
  const domainId = selectedDomain?.id;

  // ✅ Fonction de fetch SANS filters dans les dépendances
  const fetchMouvements = useCallback(async (page = 1, pageSize = null, filtersToUse = null) => {
    if (!domainId) {
      setLoading(false);
      setInitialLoading(false);
      return;
    }

    try {
      if (!initialLoading && isFirstRender.current === false) {
        setLoading(true);
      }
      
      setIsOffline(false);
      setError(null);

      const currentPageSize = pageSize || pagination.pageSize;
      
      // ✅ Utiliser les filtres passés en paramètre OU l'état actuel
      const filtersToApply = filtersToUse || filters;
      
      const response = await getMouvementsFinanciers(domainId, {
        ...filtersToApply,
        pageNumber: page,
        pageSize: currentPageSize
      });

      setMouvements(response.items || []);
      
      const totalCount = response.totalCount || 0;
      const totalPages = Math.max(1, Math.ceil(totalCount / currentPageSize));
      
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        pageSize: currentPageSize,
        totalCount: totalCount,
        totalPages: totalPages
      }));

    } catch (err) {
      console.error("Erreur fetchMouvements:", err);
      
      const isConnectionError = !err.response || 
          err.code === 'ECONNABORTED' || 
          err.message?.includes('Network Error') ||
          err.message?.includes('Failed to fetch');
      
      if (isConnectionError) {
        setIsOffline(true);
        setError(null);
      } else {
        setError("Erreur lors du chargement des données");
        setIsOffline(false);
      }
      
    } finally {
      setLoading(false);
      setInitialLoading(false);
      isFirstRender.current = false;
    }
  }, [domainId, pagination.pageSize]); // ✅ SANS filters et initialLoading

  // ✅ Effet pour le chargement initial UNIQUEMENT
  useEffect(() => {
    if (domainId && isFirstRender.current) {
      fetchMouvements(1, null, filters);
    }
  }, [domainId]); // ✅ Seulement domainId

  // ✅ Effet pour les changements de filtres AVEC DEBOUNCE
  useEffect(() => {
    // Skip premier render
    if (isFirstRender.current) return;

    // Clear timeout précédent
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // ✅ Debounce de 600ms avant de fetch
    fetchTimeoutRef.current = setTimeout(() => {
      if (domainId) {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        fetchMouvements(1, null, filters);
      }
    }, 600);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [filters, domainId, fetchMouvements]);

  // ✅ Handler pour les filtres - NE déclenche PAS de fetch immédiat
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  }, []);

  const handleRetry = () => {
    setInitialLoading(false);
    fetchMouvements(1, null, filters);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.totalPages || isOffline) return;
    fetchMouvements(page, null, filters);
  };

  const handlePageSizeChange = (newSize) => {
    const validatedSize = Math.max(10, newSize);
    if (!isOffline) {
      fetchMouvements(1, validatedSize, filters);
    }
  };

  const handleViewDocument = (mouvement) => {
    if (isOffline) return;
    setSelectedHistoric(mouvement);
    setShowDocumentModal(true);
  };

  if (initialLoading) {
    return (
      <div>
        <div className="row mb-3">
          <div className="col-12">
            <MouvementFilters
              onFilterChange={handleFilterChange}
              isLoading={true}
              initialFilters={filters}
              disabled={true}
            />
          </div>
        </div>
        
        <div className="row">
          <div className="col-xl-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold">Mouvements</h6>
                <small className="text-muted">Chargement...</small>
              </div>
              <div className="card-body p-0">
                <TableSkeleton columns={9} rows={7} showHeader={true} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {isOffline && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between py-3 mb-3 shadow-sm">
          <div className="d-flex align-items-center">
            <FaWifi className="me-3 fs-5" />
            <div>
              <strong className="d-block">Mode hors ligne</strong>
              <small className="text-muted">Impossible de charger les données. Vérifiez votre connexion.</small>
            </div>
          </div>
          <button onClick={handleRetry} className="btn btn-sm btn-warning">
            <FaSyncAlt className="me-2" />
            Réessayer
          </button>
        </div>
      )}

      {error && !isOffline && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between py-3 mb-3 shadow-sm">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-3 fs-5"></i>
            <div>
              <strong className="d-block">Erreur</strong>
              <small>{error}</small>
            </div>
          </div>
          <button onClick={handleRetry} className="btn btn-sm btn-danger">
            <FaSyncAlt className="me-2" />
            Réessayer
          </button>
        </div>
      )}

      <div className="row">
        <div className="col-12">
          <MouvementFilters
            onFilterChange={handleFilterChange}
            isLoading={loading}
            initialFilters={filters}
            disabled={isOffline}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-xl-12">
          <div className={`card border-0 shadow-sm ${isOffline ? 'border-warning' : ''}`}>
            <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
              <div>
                <h6 className="mb-0 fw-bold">Mouvements</h6>
                <small className="text-muted">
                  {loading ? 'Mise à jour...' : 
                   isOffline ? 'Mode hors ligne' :
                   `${pagination.totalCount} résultat${pagination.totalCount > 1 ? 's' : ''}`}
                </small>
              </div>
              {loading && !initialLoading && (
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
              )}
            </div>
            
            <div className="card-body p-0">
              {loading && !initialLoading ? (
                <TableSkeleton columns={9} rows={5} showHeader={false} />
              ) : mouvements.length === 0 ? (
                <div className="text-center py-5">
                  <div className="bg-light rounded-3 px-4 py-5 mx-auto" style={{ maxWidth: "450px" }}>
                    <i className="fa-solid fa-receipt fa-4x mb-4 text-muted opacity-50"></i>
                    <h5 className="fw-bold mb-2">
                      {isOffline ? "Connexion interrompue" : 
                       error ? "Erreur de chargement" :
                       "Aucun mouvement trouvé"}
                    </h5>
                    <p className="text-muted mb-3">
                      {isOffline ? "Les données ne peuvent pas être chargées pour le moment." :
                       error ? "Une erreur est survenue lors du chargement." :
                       "Aucune transaction ne correspond à vos critères de recherche."}
                    </p>
                    {(isOffline || error) && (
                      <button className="btn btn-primary" onClick={handleRetry}>
                        <FaSyncAlt className="me-2" />
                        Réessayer
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-striped mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Compte</th>
                        <th>Catégorie</th>
                        <th className="text-end">Quantité</th>
                        <th className="text-end">Valeur</th>
                        <th className="text-end">Libellé</th>
                        <th className="text-end">Impôt</th>
                        <th className="text-center">Description</th>
                        <th className="text-end">Date</th>
                        <th className="text-center">Doc</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mouvements.map(mouvement => (
                        <MouvementTableRow 
                          key={mouvement.id} 
                          mouvement={mouvement} 
                          onViewDocument={handleViewDocument}
                          isOffline={isOffline}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {mouvements.length > 0 && !isOffline && (
              <div className="border-top">
                <MouvementPagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  disabled={loading}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedHistoric && !isOffline && (
        <DocumentViewerModal
          show={showDocumentModal}
          onHide={() => {
            setShowDocumentModal(false);
            setSelectedHistoric(null);
          }}
          historicId={selectedHistoric.id}
          domainId={selectedDomain?.id}
          accountHistoric={selectedHistoric}
        />
      )}
    </div>
  );
};

export default MouvementFinancierTable;